import { type ColorCorrectionOptions, type NodeId } from 'web';
import { ColorCorrectionData } from '@/types/node';
import { FlowGraph } from '../FlowGraph';

export type ColorCorrectionField = keyof ColorCorrectionOptions;

const DEFAULT_OPTIONS: ColorCorrectionOptions = {
  exposure: 0,
  contrast: 0,
  saturation: 0,
  temperature: 0,
  tint: 0,
  highlights: 0,
  shadows: 0,
};

export class ColorCorrectionView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  private getOptions(): ColorCorrectionOptions | undefined {
    return this.graph.store.getNodeDataNullable<ColorCorrectionData>(this.nodeId)?.options;
  }

  private setOption(field: ColorCorrectionField, value: number, ongoing: boolean) {
    const options = this.getOptions();
    if (!options) return;
    this.graph.wasmEdit((wasm) => {
      return wasm.setColorCorrectionOptions(this.nodeId, { ...options, [field]: value });
    }, ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
  }

  setExposure(value: number, ongoing: boolean) {
    this.setOption('exposure', value, ongoing);
  }

  setContrast(value: number, ongoing: boolean) {
    this.setOption('contrast', value, ongoing);
  }

  setSaturation(value: number, ongoing: boolean) {
    this.setOption('saturation', value, ongoing);
  }

  setTemperature(value: number, ongoing: boolean) {
    this.setOption('temperature', value, ongoing);
  }

  setTint(value: number, ongoing: boolean) {
    this.setOption('tint', value, ongoing);
  }

  setHighlights(value: number, ongoing: boolean) {
    this.setOption('highlights', value, ongoing);
  }

  setShadows(value: number, ongoing: boolean) {
    this.setOption('shadows', value, ongoing);
  }

  reset() {
    this.graph.wasmEdit((wasm) => wasm.setColorCorrectionOptions(this.nodeId, DEFAULT_OPTIONS));
    this.graph.updateNodeOutputs(this.nodeId);
  }
}
